import { App, Button, Form, Input, Radio, Space } from "antd";
import { useOnboarding } from "hooks/useOnboarding";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { mutate } from "swr";
import {
	trackAttributionGateShown,
	trackAttributionGateSignedOut,
} from "utils/activationAnalytics";
import { logout } from "utils/firebase";
import { getErrorMsg } from "utils/geterror";
import { AuthPage } from "../components/AuthPage";
import {
	SIGNUP_ATTRIBUTION_CHANNELS,
	SIGNUP_ATTRIBUTION_OTHER_MAX,
	type SignupAttributionChannel,
	validateAttribution,
} from "./attributionOptions";

// Type assertion for Helmet component
const HelmetComponent = Helmet as any;

/**
 * "How did you find us?" (EQ-2266): shown once to a new self sign-up, before
 * email verification and before the product tour.
 */
export function SignupAttribution() {
	const { notification } = App.useApp();
	const navigate = useNavigate();
	const { submitAttribution } = useOnboarding();
	const [channel, setChannel] = useState<SignupAttributionChannel>();
	const [otherText, setOtherText] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	// The "needs text" error belongs under the text box, not the choices.
	const otherError = Boolean(error) && channel === "other";

	// This gate renders in place from Protected.tsx — no route change, so no
	// $pageview. Without this event there is no record that it was ever seen.
	useEffect(() => {
		trackAttributionGateShown();
	}, []);

	const onContinue = async () => {
		const problem = validateAttribution(channel, otherText);
		setError(problem);
		if (problem || !channel) return;

		setSaving(true);
		try {
			// 🪤 Answering the gate emits NOTHING from here. The one event for a
			// given answer — `signup_attribution_submitted` — is emitted inside
			// `submitAttribution`, after its own try/catch, which is the only place
			// that can tell a saved answer apart from a re-submit of one already
			// recorded (EQU-401). This call site cannot: it sees "no throw" for both.
			// Adding a capture here again makes one answer read as two.
			await submitAttribution(
				channel,
				channel === "other" ? otherText.trim() : null,
			);
		} catch (err) {
			notification.error(getErrorMsg(err));
		} finally {
			setSaving(false);
		}
	};

	const onSignOut = async () => {
		trackAttributionGateSignedOut();
		logout();
		await mutate(() => true, undefined, { revalidate: false });
		navigate("/signin");
	};

	return (
		<AuthPage>
			<HelmetComponent>
				<title>Equility - How did you find us?</title>
			</HelmetComponent>
			<div className="auth-card">
				<div className="auth-card__head">
					<h1 className="auth-card__title">How did you find Equility?</h1>
					<p className="auth-card__subtitle">
						One quick question before you get started.
					</p>
				</div>
				<Form layout="vertical" onFinish={onContinue}>
					<Form.Item
						validateStatus={error && !otherError ? "error" : undefined}
						help={error && !otherError ? error : undefined}
					>
						<Radio.Group
							value={channel}
							onChange={(e) => {
								setChannel(e.target.value);
								setError(null);
							}}
						>
							<Space direction="vertical">
								{SIGNUP_ATTRIBUTION_CHANNELS.map((c) => (
									<Radio key={c.key} value={c.key}>
										{c.label}
									</Radio>
								))}
							</Space>
						</Radio.Group>
					</Form.Item>
					{channel === "other" && (
						<Form.Item
							label="Where did you find us?"
							htmlFor="found-us-other"
							validateStatus={otherError ? "error" : undefined}
							help={otherError ? error : undefined}
						>
							<Input
								id="found-us-other"
								autoFocus
								maxLength={SIGNUP_ATTRIBUTION_OTHER_MAX}
								value={otherText}
								onChange={(e) => {
									setOtherText(e.target.value);
									setError(null);
								}}
							/>
						</Form.Item>
					)}
					<Button type="primary" htmlType="submit" block loading={saving}>
						Continue
					</Button>
				</Form>
				<div className="auth-card__actions">
					<Button type="link" onClick={onSignOut}>
						Sign out
					</Button>
				</div>
			</div>
		</AuthPage>
	);
}
